import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { displayDate } from "@/lib/evidencePresentation";

export const localityDistance = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? `About ${Math.round(value / 10) * 10} m straight-line`
    : "Distance unavailable";

export function LocalityRecords({ locality, saved = false }) {
  if (!locality) return null;
  return (
    <div className="locality-records">
      <p className="source">
        {saved ? "Saved snapshot" : "Loaded"}{" "}
        {displayDate(locality.prepared_at)} · {locality.scope}
      </p>
      <div className="locality-grid">
        {locality.layers.map((layer) => (
          <section
            key={layer.id}
            className={`locality-layer locality-${layer.id}`}
            aria-label={layer.label}
          >
            <div className="locality-layer-title">
              <h3>{layer.label}</h3>
              <span className={`evidence-status status-${layer.status}`}>
                {layer.status === "unavailable"
                  ? "Source unavailable"
                  : layer.status === "stale"
                    ? "Older source snapshot"
                    : "Sourced locations"}
              </span>
            </div>
            <p>{layer.scope}</p>
            {layer.status === "unavailable" ? (
              <p className="notice">{layer.message}</p>
            ) : (
              <>
                <p className="source">
                  Showing {layer.items.length} of {layer.matching_record_count}{" "}
                  mapped records within 1 km.
                </p>
                {layer.items.length === 0 ? (
                  <p>
                    No records were returned within this radius in this dataset.
                    This does not establish that nearby services or parks are
                    absent.
                  </p>
                ) : (
                  <ol>
                    {layer.items.map((item) => (
                      <li key={item.id}>
                        <strong>{item.name}</strong>
                        <span>{localityDistance(item.distance_m)}</span>
                        <small>Source record {item.id}</small>
                      </li>
                    ))}
                  </ol>
                )}
                <p className="source">{layer.distance_method}</p>
                <p className="source">
                  Source period {displayDate(layer.observation_period)} ·
                  Retrieved {displayDate(layer.retrieved_at)} ·{" "}
                  {layer.attribution}
                </p>
              </>
            )}
            <a href={layer.source_url}>View council source</a>
          </section>
        ))}
      </div>
      <p className="source">
        These location records do not assess transport service quality or
        overall neighbourhood suitability. Planning and flood assessments remain
        separate.
      </p>
    </div>
  );
}

export default function LocalityPanel({
  inputs,
  location,
  locality,
  onChange,
  selectedLayers,
  onSelectedLayers,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const version = useRef(0);
  useEffect(
    () => () => {
      version.current += 1;
    },
    [],
  );
  const eligible = Boolean(
    inputs.address_id &&
      location?.status === "observed" &&
      location?.dataset_version,
  );
  const autoLoaded = useRef("");
  const load = useCallback(async () => {
    const request = ++version.current;
    setBusy(true);
    setError("");
    onChange(null);
    try {
      const { data } = await api.post("/research/locality", {
        address: inputs.address,
        address_id: inputs.address_id,
        postcode: inputs.postcode || null,
        expected_location_version: location.dataset_version,
      });
      if (request === version.current) {
        onChange(data);
        onSelectedLayers(
          data.layers
            .filter((layer) => layer.status !== "unavailable")
            .map((layer) => layer.id),
        );
      }
    } catch (e) {
      if (request === version.current)
        setError(
          typeof e.response?.data?.detail === "string"
            ? e.response.data.detail
            : "Nearby sources are unavailable. Your property report is still available; try loading locality again.",
        );
    } finally {
      if (request === version.current) setBusy(false);
    }
  }, [
    inputs.address,
    inputs.address_id,
    inputs.postcode,
    location?.dataset_version,
    onChange,
    onSelectedLayers,
  ]);
  useEffect(() => {
    const key = [inputs.address_id, location?.dataset_version].join(":");
    if (!eligible || locality || busy || autoLoaded.current === key) return;
    autoLoaded.current = key;
    load();
  }, [
    busy,
    eligible,
    inputs.address_id,
    load,
    locality,
    location?.dataset_version,
  ]);
  return (
    <section
      className="locality-section"
      id="locality"
      aria-label="Nearby locality records"
    >
      <div className="locality-header">
        <div>
          <span className="eyebrow">Around the matched parcel</span>
          <h2>Get to know the neighbourhood.</h2>
          <p>
            Explore council bus-stop and park records within 1 km. Distances are
            straight-line, not walking routes.
          </p>
        </div>
        <button type="button" disabled={!eligible || busy} onClick={load}>
          {busy
            ? "Loading nearby records…"
            : locality
              ? "Reload locality records"
              : "Load nearby records"}
        </button>
      </div>
      {!eligible && (
        <p className="notice">
          Select a council address and prepare a report with a current mapped
          coordinate to load nearby records.
        </p>
      )}
      {busy && (
        <p role="status">
          Checking the two council sources. Each source is assessed separately.
        </p>
      )}
      {error && (
        <p role="alert" className="error-state">
          {error}
        </p>
      )}
      {locality && (
        <>
          <fieldset className="locality-toggles">
            <legend>Show on the map</legend>
            {locality.layers.map((layer) => (
              <label className="checkbox" key={layer.id}>
                <input
                  type="checkbox"
                  checked={
                    selectedLayers.includes(layer.id) &&
                    layer.status !== "unavailable"
                  }
                  disabled={
                    layer.status === "unavailable" || layer.items.length === 0
                  }
                  onChange={(e) =>
                    onSelectedLayers(
                      e.target.checked
                        ? [...selectedLayers, layer.id]
                        : selectedLayers.filter((id) => id !== layer.id),
                    )
                  }
                />
                <span>
                  <i aria-hidden="true" className={`layer-dot ${layer.id}`} />
                  {layer.label}
                  {layer.status === "stale" ? " · older snapshot" : ""}
                </span>
              </label>
            ))}
            <a href="#overview">View selected layers on map ↑</a>
          </fieldset>
          <LocalityRecords locality={locality} />
        </>
      )}
    </section>
  );
}
